import assertExhaustive from '@bybeam/platform-lib/utilities/assertExhaustive';
import {
  ApplicantType,
  ApplicantTypeRole,
  ApplicationConfig,
  ApplicationConfigurationKey,
  Program,
  User,
} from '@bybeam/platform-types';
import baltimoreConfig from './baltimore/config.js';
import batonConfig from './batonConfig.js';
import celledT1Config from './calgrows/careerBuilder.js';
import celledT2Config from './calgrows/learnAndEarn.js';
import hcaiConfig from './caring4cal/hcaiConfig.js';
import hcaiT1Config from './caring4cal/hcaiTrack1.js';
import hcaiT2Config from './caring4cal/hcaiTrack2.js';
import childcareConfig from './demoEnv/childcare.js';
import multipartyConfig from './demoEnv/multiparty.js';
import workforceConfig from './demoEnv/workforce.js';
import eduAddressConfig from './eduPartners/addressConfig.js';
import eduConfig from './eduPartners/baseConfig.js';
import comptonDualConfig from './eduPartners/comptonDualConfig.js';
import eduEmergency from './eduPartners/dcpsEmergency.js';
import eduMicrogrant from './eduPartners/dcpsMicrogrant.js';
import delmarConfig from './eduPartners/delmarConfig.js';
import eduECMC from './eduPartners/ecmcConfig.js';
import lcccConfig from './eduPartners/lcccConfig.js';
import matcConfig from './eduPartners/matcConfig.js';
import mecConfig from './eduPartners/mecConfig.js';
import minnesotaConfig from './eduPartners/minnesotaConfig.js';
import nccpcConfig from './eduPartners/nccpcConfig.js';
import eduNoDocs from './eduPartners/noDocsConfig.js';
import oppnetConfig from './eduPartners/oppnetConfig.js';
import eduPG from './eduPartners/pgConfig.js';
import fairfaxConfig from './fairfax/config.js';
import fairfaxEnrollConfig from './fairfax/enrollmentConfig.js';
import kc1MbbConfig from './kc/1mbbConfig.js';
import kcBizcareConfig from './kc/bizCareConfig.js';
import kcEDCConfig from './kc/edcConfig.js';
import kcKivaConfig from './kc/kivaConfig.js';
import kcLidoConfig from './kc/lidoConfig.js';
import kcSMBCapitalConfig from './kc/smbCapitalConfig.js';
import kcSMBGrantConfig from './kc/smbGrantConfig.js';
import kcSMBIDAConfig from './kc/smbIDAConfig.js';
import kcSMBLoanPrepConfig from './kc/smbLoanPrepConfig.js';
import nhConfig from './nh/config.js';
import tppNYCConfig from './tppNYCConfig.js';
import tppPhillyConfig from './tppPhillyConfig.js';

export function getFirstPartyApplicationConfig(program: Program): ApplicationConfig {
  const firstPartyApplicantType = program.applicantTypes?.find(
    ({ applicantType: { role } }) => role === ApplicantTypeRole.FirstParty,
  );
  return getApplicationConfiguration(program, firstPartyApplicantType.applicantType);
}

export function getApplicationConfiguration(program: Program, user: User): ApplicationConfig;
export function getApplicationConfiguration(
  program: Program,
  applicantType: ApplicantType,
): ApplicationConfig;
export function getApplicationConfiguration(
  program: Program,
  user: User | ApplicantType,
): ApplicationConfig {
  const applicantType =
    user && 'applicantProfile' in user ? user.applicantProfile?.applicantType : user;

  if (!program.applicationConfigurations?.length) return getFallbackAppConfig(program);

  const config = program.applicationConfigurations.find(
    ({ applicantTypeId }) => applicantTypeId === applicantType.id,
  );
  return config.configuration;
}

function getFallbackAppConfig(program: Program): ApplicationConfig {
  switch (program.config.applicationConfiguration) {
    // Beam Demo Configs
    case ApplicationConfigurationKey.DEMO_CHILDCARE:
    case ApplicationConfigurationKey.WESTSIDE_COUNTY_V1:
      return childcareConfig;
    case ApplicationConfigurationKey.DEMO_WORKFORCE:
    case ApplicationConfigurationKey.WESTSIDE_COUNTY_V2:
      return workforceConfig;
    case ApplicationConfigurationKey.DEMO_MULTIPARTY:
      return multipartyConfig;

    // Government Partners
    case ApplicationConfigurationKey.BATON:
      return batonConfig;
    case ApplicationConfigurationKey.BALTIMORE:
      return baltimoreConfig;
    case ApplicationConfigurationKey.CELL_ED_T1:
      return celledT1Config;
    case ApplicationConfigurationKey.CELL_ED_T2:
      return celledT2Config;
    case ApplicationConfigurationKey.EDC_KC:
      return kcEDCConfig;
    case ApplicationConfigurationKey.FAIRFAX:
      return fairfaxConfig;
    case ApplicationConfigurationKey.FAIRFAX_ENROLL:
      return fairfaxEnrollConfig;
    case ApplicationConfigurationKey.HCAI:
      return hcaiConfig;
    case ApplicationConfigurationKey.HCAI_T1:
      return hcaiT1Config;
    case ApplicationConfigurationKey.HCAI_T2:
      return hcaiT2Config;
    case ApplicationConfigurationKey.KC_BIZCARE:
      return kcBizcareConfig;
    case ApplicationConfigurationKey.NH:
      return nhConfig;
    case ApplicationConfigurationKey.KC_SMB_CAPITAL:
      return kcSMBCapitalConfig;
    case ApplicationConfigurationKey.KC_SMB_GRANT:
      return kcSMBGrantConfig;
    case ApplicationConfigurationKey.KC_SMB_IDA:
      return kcSMBIDAConfig;
    case ApplicationConfigurationKey.KC_SMB_LOAN_PREP:
      return kcSMBLoanPrepConfig;
    case ApplicationConfigurationKey.KC_KIVA:
      return kcKivaConfig;
    case ApplicationConfigurationKey.KC_1MBB:
      return kc1MbbConfig;
    case ApplicationConfigurationKey.KC_LIDO:
      return kcLidoConfig;
    case ApplicationConfigurationKey.TPP_PHILLY:
      return tppPhillyConfig;
    case ApplicationConfigurationKey.TPP_NYC:
      return tppNYCConfig;

    // EDU/Philanthropy Partners
    case ApplicationConfigurationKey.EDU:
      return eduConfig;
    case ApplicationConfigurationKey.EDU_ADDRESS:
      return eduAddressConfig;
    case ApplicationConfigurationKey.EDU_DELMAR:
      return delmarConfig;
    case ApplicationConfigurationKey.EDU_DCPS_MICROGRANT:
      return eduMicrogrant;
    case ApplicationConfigurationKey.EDU_DCPS_EMERGENCY:
      return eduEmergency;
    case ApplicationConfigurationKey.EDU_ECMC:
      return eduECMC;
    case ApplicationConfigurationKey.EDU_LCCC:
      return lcccConfig;
    case ApplicationConfigurationKey.EDU_MATC:
      return matcConfig;
    case ApplicationConfigurationKey.EDU_NCCPC:
      return nccpcConfig;
    case ApplicationConfigurationKey.EDU_NO_DOCS:
      return eduNoDocs;
    case ApplicationConfigurationKey.EDU_PG:
      return eduPG;
    case ApplicationConfigurationKey.MICHIGAN_EDUCATION_CORPS:
      return mecConfig;
    case ApplicationConfigurationKey.OPPNET:
      return oppnetConfig;
    case ApplicationConfigurationKey.EDU_COMPTON_DUAL:
      return comptonDualConfig;
    case ApplicationConfigurationKey.EDU_MINNESOTA:
      return minnesotaConfig;

    default:
      return assertExhaustive(program.config.applicationConfiguration);
  }
}
